import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { API_BASE_URL } from '../config/api';
import { Save, CreditCard, CheckCircle } from 'lucide-react';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

export default function AdminMembershipPlans() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/'); return; }
    fetchPlans();
  }, [user, authLoading]);

  const fetchPlans = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/membership/plans`);
      setPlans(data);
      const initial = {};
      data.forEach(p => {
        initial[p.id] = {
          registration_fee: p.registration_fee ?? '',
          price: p.price ?? '',
          featuresText: (p.features || []).join('\n')
        };
      });
      setEdits(initial);
    } catch (e) {
      console.error('Error fetching plans:', e);
    } finally {
      setLoading(false);
    }
  };

  const setField = (planId, field, value) => {
    setEdits(prev => ({ ...prev, [planId]: { ...prev[planId], [field]: value } }));
    setSavedId(null);
  };

  const savePlan = async (planId) => {
    setSavingId(planId);
    try {
      const e = edits[planId];
      await axios.put(`${API_BASE_URL}/api/membership/admin/plans/${planId}`, {
        registration_fee: e.registration_fee === '' ? null : Number(e.registration_fee),
        price: Number(e.price),
        features: e.featuresText.split('\n')
      }, { headers: authHeaders(), withCredentials: true });
      setSavedId(planId);
      await fetchPlans();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A5F]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="admin-plans-title">Membership Plans</h1>
        <p className="text-gray-600">Edit fees and benefits. Changes reflect immediately on the public Membership page.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {plans.map((plan) => (
          <Card key={plan.id} className="p-6" data-testid={`admin-plan-card-${plan.id}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#1E3A5F]/10 rounded-full"><CreditCard className="h-5 w-5 text-[#1E3A5F]" /></div>
                <div>
                  <h3 className="font-bold text-[#1E3A5F]">{plan.plan_name}</h3>
                  <Badge variant="outline" className="text-xs capitalize">{plan.plan_type}{plan.tier_level ? ` • Tier ${plan.tier_level}` : ''}</Badge>
                </div>
              </div>
              {savedId === plan.id && (
                <span className="text-green-600 text-sm flex items-center gap-1"><CheckCircle className="h-4 w-4" />Saved</span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registration Fee (₹)</label>
                <Input
                  type="number" min="0"
                  value={edits[plan.id]?.registration_fee ?? ''}
                  onChange={(e) => setField(plan.id, 'registration_fee', e.target.value)}
                  data-testid={`plan-regfee-input-${plan.id}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Annual Fee (₹)</label>
                <Input
                  type="number" min="0"
                  value={edits[plan.id]?.price ?? ''}
                  onChange={(e) => setField(plan.id, 'price', e.target.value)}
                  data-testid={`plan-annualfee-input-${plan.id}`}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Benefits (one per line)</label>
              <Textarea
                rows={6}
                value={edits[plan.id]?.featuresText ?? ''}
                onChange={(e) => setField(plan.id, 'featuresText', e.target.value)}
                data-testid={`plan-features-input-${plan.id}`}
              />
            </div>

            <Button
              onClick={() => savePlan(plan.id)}
              disabled={savingId === plan.id}
              className="bg-[#E87722] hover:bg-[#d66a1a]"
              data-testid={`plan-save-btn-${plan.id}`}
            >
              <Save className="h-4 w-4 mr-2" />
              {savingId === plan.id ? 'Saving...' : 'Save Plan'}
            </Button>
          </Card>
        ))}
      </div>
    </AdminLayout>
  );
}
